import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";
import { useTheme } from "../context/ThemeContext";
import {
  LayoutDashboard,
  Package,
  ShoppingBag,
  Grid3X3,
  User,
  CreditCard,
  LogOut,
  Store,
  ChevronRight,
  X,
  Building2,
} from "lucide-react";
import Badge from "./ui/Badge";

const NAV_ITEMS = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Overview", exact: true },
  { to: "/dashboard/products", icon: Package, label: "Products" },
  { to: "/dashboard/incoming-orders", icon: ShoppingBag, label: "Incoming Orders" },
  { to: "/dashboard/categories", icon: Grid3X3, label: "Categories" },
  { to: "/dashboard/profile", icon: User, label: "Profile" },
  // { to: "/dashboard/notifications", icon: Bell, label: "Notifications" },
  { to: "/dashboard/pricing", icon: CreditCard, label: "Pricing" },
];

export default function Sidebar({ mobileOpen, onClose }) {
  const { user, logout } = useAuth();
  const { unreadCount } = useApp();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const notifs = unreadCount(user?.id);
  const hasTenantAccess = user?.hasTenantAccess === true || Boolean(user?.tenantId);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div
        className={`px-5 pt-6 pb-5 border-b ${
          isDark ? "border-gray-800" : "border-gray-200"
        }`}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-glow-violet flex-shrink-0">
            <Store size={18} className="text-white" />
          </div>
          <div className="min-w-0">
            <p
              className={`text-sm font-bold truncate ${
                isDark ? "text-gray-100" : "text-gray-900"
              }`}>
              ShopOwnaa
            </p>
            <p
              className={`text-xs truncate ${
                isDark ? "text-gray-500" : "text-gray-400"
              }`}>
              Marketplace Platform
            </p>
          </div>
        </div>
      </div>

      {/* Tenant info */}
      <div
        className={`px-4 py-4 border-b ${
          isDark ? "border-gray-800" : "border-gray-200"
        }`}>
        <div
          className={`flex items-center gap-3 rounded-xl p-3 ${
            isDark ? "bg-gray-800/50" : "bg-gray-100"
          }`}>
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
            {user?.initials}
          </div>
          <div className="min-w-0 flex-1">
            <p
              className={`text-sm font-semibold truncate ${
                isDark ? "text-gray-100" : "text-gray-900"
              }`}>
              {user?.name?.split(" ")[0]}
            </p>
            <p
              className={`text-xs truncate ${
                isDark ? "text-gray-500" : "text-gray-400"
              }`}>
              {user?.business}
            </p>
          </div>
          {user?.plan && <Badge variant={user?.plan}>{user?.plan}</Badge>}
        </div>
      </div>

      {/* Nav Links */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        <p
          className={`text-[10px] font-semibold uppercase tracking-widest px-3 mb-2 ${
            isDark ? "text-gray-600" : "text-gray-400"
          }`}>
          Navigation
        </p>
        {NAV_ITEMS.map(({ to, icon: Icon, label, exact }) => (
          <NavLink
            key={to}
            to={to}
            end={exact}
            onClick={onClose}
            className={({ isActive }) =>
              `sidebar-link group ${
                isActive ? "sidebar-link-active" : "sidebar-link-inactive"
              }`
            }>
            {({ isActive }) => (
              <>
                <Icon
                  size={17}
                  className={
                    isActive
                      ? "text-violet-400"
                      : isDark
                        ? "text-gray-500 group-hover:text-gray-300"
                        : "text-gray-400 group-hover:text-gray-600"
                  }
                />
                <span className="flex-1">{label}</span>
                {label === "Notifications" && notifs > 0 && (
                  <span className="bg-violet-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                    {notifs}
                  </span>
                )}
                {isActive && (
                  <ChevronRight
                    size={14}
                    className="text-violet-400 opacity-60"
                  />
                )}
              </>
            )}
          </NavLink>
        ))}

        {/* Tenant Property Management link – tenants only */}
        {hasTenantAccess && (
          <>
            <p
              className={`text-[10px] font-semibold uppercase tracking-widest px-3 mt-4 mb-2 ${
                isDark ? "text-gray-600" : "text-gray-400"
              }`}>
              Property
            </p>
            <button
              onClick={() => {
                onClose();
                navigate("/tenant");
              }}
              className={`sidebar-link sidebar-link-inactive w-full group`}>
              <Building2
                size={17}
                className={
                  isDark
                    ? "text-gray-500 group-hover:text-gray-300"
                    : "text-gray-400 group-hover:text-gray-600"
                }
              />
              <span className="flex-1 text-left">Tenant Property</span>
              <ChevronRight size={14} className="opacity-40" />
            </button>
          </>
        )}
      </nav>

      {/* Bottom – plan chip + logout */}
      <div
        className={`px-3 pb-5 border-t pt-4 space-y-2 ${
          isDark ? "border-gray-800" : "border-gray-200"
        }`}>
        {user?.plan === "Free" && (
          <NavLink
            to="/dashboard/pricing"
            onClick={onClose}
            className={`flex items-center gap-2 border rounded-xl px-3 py-2.5 text-xs font-semibold transition-all duration-200 w-full ${
              isDark
                ? "bg-violet-600/10 hover:bg-violet-600/20 border-violet-600/25 text-violet-400"
                : "bg-violet-50 hover:bg-violet-100 border-violet-200 text-violet-600"
            }`}>
            <CreditCard size={14} />
            Upgrade Plan
            <ChevronRight size={12} className="ml-auto" />
          </NavLink>
        )}
        <button
          onClick={handleLogout}
          className="sidebar-link sidebar-link-inactive w-full text-rose-500 hover:text-rose-500 hover:bg-rose-50 dark:text-rose-400 dark:hover:text-rose-300 dark:hover:bg-rose-500/10">
          <LogOut size={17} />
          Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex flex-col w-60 xl:w-64 border-r h-screen sticky top-0 flex-shrink-0 transition-colors duration-300 ${
          isDark ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200"
        }`}>
        <SidebarContent />
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />
          <div
            className={`relative w-64 border-r h-full z-50 animate-slide-in overflow-y-auto transition-colors duration-300 ${
              isDark
                ? "bg-gray-900 border-gray-800"
                : "bg-white border-gray-200"
            }`}>
            <button
              onClick={onClose}
              className={`absolute top-4 right-4 p-1.5 rounded-lg transition-colors ${
                isDark
                  ? "text-gray-400 hover:text-gray-200 hover:bg-gray-800"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              }`}>
              <X size={18} />
            </button>
            <SidebarContent />
          </div>
        </div>
      )}
    </>
  );
}
